// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../components/Dashboard/Sidebar';
import Navbar from '../components/Dashboard/Navbar';
import DashboardMain from '../components/Dashboard/DashboardMain';
import PropertySearch from '../components/Dashboard/PropertySearch';
import AnalysisResults from '../components/Dashboard/AnalysisResults';
import ListView from '../components/Dashboard/ListView';
import SharedProperties from '../components/Dashboard/SharedProperties';
import UserSettings from '../components/Dashboard/UserSettings';
import Subscription from '../components/Subscriptions';
import { clearCurrentAnalysis } from '../redux/slices/analysisSlice';
import DashboardGlow from '../components/UI/DashboardGlow';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { currentAnalysis } = useSelector((state) => state.analysis);

  const [activeView, setActiveView] = useState('dashboard');
  const [isMobileOpen, setIsMobileOpen] = useState(false);


  // Auto-switch to search view ONLY when analysis completes (currentAnalysis changes to truthy)
  // Removed 'activeView' from deps to prevent re-triggering on manual navigation
  useEffect(() => {
    if (currentAnalysis) {
      setActiveView('search');
    }
  }, [currentAnalysis]);  // Only depends on currentAnalysis now

  // Clear results when navigating away from 'search' view (to vanish results on change)
  useEffect(() => {
    if (activeView !== 'search' && currentAnalysis) {
      dispatch(clearCurrentAnalysis());
    }
  }, [activeView, currentAnalysis, dispatch]);

  // Handle navigation to search view
  const handleNewSearch = () => {
    setActiveView('search');
    dispatch(clearCurrentAnalysis()); // Clear previous analysis on new search trigger
  };

  // Handle navigation to list view
  const handleNavigateToList = (view = 'list') => {
    // If called directly from an onClick, 'view' will be an Event object
    const finalView = typeof view === 'string' ? view : 'list';
    setActiveView(finalView);
  };

  // Handle navigation back to dashboard
  const handleNavigateToDashboard = (view = 'dashboard') => {
    // If called directly from an onClick, 'view' will be an Event object
    const finalView = typeof view === 'string' ? view : 'dashboard';
    setActiveView(finalView);
  };

  const handleAnalysisComplete = (analysisData) => {
    console.log('Analysis completed in Dashboard:', analysisData);
    // Analysis results will automatically show due to currentAnalysis state
  };

  const renderMainContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <DashboardMain
            onNewSearch={handleNewSearch}
            onNavigateToList={handleNavigateToList}
          />
        );
      case 'search':
        return currentAnalysis ? (
          <AnalysisResults />
        ) : (
          <PropertySearch
            onAnalysisComplete={handleAnalysisComplete}
          />
        );
      case 'list':
        return (
          <ListView
            onNavigateToDashboard={handleNavigateToDashboard}
            onNewSearch={handleNewSearch}
          />
        );
      case 'shared':
        return <SharedProperties />;
      case 'settings':
        return <UserSettings />;
      case 'billing':
        return <Subscription />;
      default:
        return (
          <DashboardMain
            onNewSearch={handleNewSearch}
            onNavigateToList={handleNavigateToList}
          />
        );
    }
  };

  return (
    <div className="flex h-screen bg-[#f8fafc] relative">
      <DashboardGlow />
      <Sidebar
        activeView={activeView}
        setActiveView={setActiveView}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar
          activeView={activeView}
          setIsMobileOpen={setIsMobileOpen}
          onNewSearch={handleNewSearch}
        />

        <main className="flex-1 overflow-y-auto p-4">
          {renderMainContent()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;